import axios from 'axios';
import { API_BASE_URL } from '../config/config';

// 대상자 한 명의 신원 정보. 나이·성별·연락처는 측정값 응답에만 실려 오므로,
// 대상자 id 만 들고 온 화면(주소로 연 케어플랜 등)은 여기서 받아야 한다.
//
// 슈퍼 관리자는 엔드포인트가 다르다 — DashboardDetail 이 하는 것과 같은 분기다.
// 관리자 확인이 실패하면 일반 경로로 떨어진다(권한이 낮은 쪽이 안전하다).
// 측정 기록이 하나도 없는 대상자는 신원 행 자체가 없어 null 을 돌려준다.
export const fetchPatientBasics = async ({ token, appUserId }) => {
    const headers = { Authorization: `Bearer ${token}` };

    let isSuperAdmin = false;
    try {
        const adminCheck = await axios.get(`${API_BASE_URL}/api/check-admin-email`, { headers });
        isSuperAdmin = adminCheck.data?.isSuperAdmin || false;
    } catch (error) {
        isSuperAdmin = false;
    }

    const endpoint = isSuperAdmin
        ? `${API_BASE_URL}/api/health-data/${appUserId}/super-admin`
        : `${API_BASE_URL}/api/health-data/${appUserId}`;

    const response = await axios.get(endpoint, { headers });
    const rows = response.data?.success ? (response.data.data || []) : [];
    const first = rows[0];
    if (!first) return null;

    return {
        app_user_id: appUserId,
        user_id: first.user_id,
        name: first.name,
        birthdate: first.birthdate,
        gender: first.gender,
        mobile: first.mobile,
        management_group: first.management_group,
        has_smart_ring: first.has_smart_ring === true
    };
};

export const PATIENTS_PAGE_SIZE = 10;

export const ALL_MEASURE_TYPES = [
    'BLOOD_GLUCOSE',
    'BLOOD_GLUCOSE_MEAL',
    'BP_DIASTOLIC',
    'BP_SYSTOLIC',
    'BMI'
];

// /api/patients 응답 한 건을 화면들이 쓰던 사용자 객체 모양으로 되돌린다.
//
// getRiskLevel 과 allowedTypes 를 인자로 받는 이유: 이 저장소에는 getRiskLevel 이
// 페이지마다 따로 정의돼 있고 임계값도 라벨도 서로 다르다. 여기서 하나를 골라
// 박아 두면 그 함수를 쓰는 화면의 색깔이 조용히 바뀐다.
//
// 지금은 호출자가 Dashboard 하나뿐이라 인자를 없앨 수도 있지만 그대로 둔다 —
// 화면마다 기준이 다르다는 사실이 사라진 게 아니라 화면이 하나 줄었을 뿐이다.
export const toPatientRow = (item, { getRiskLevel, allowedTypes = ALL_MEASURE_TYPES }) => {
    const row = {
        id: item.id,
        app_user_id: item.appUserId,
        name: item.name,
        birthdate: item.birthdate,
        mobile: item.mobile,
        gender: item.gender,
        group_name: item.groupName,
        management_service_yn: item.managementServiceYn,
        focuspatient: item.focusPatient,
        last_modified_date: item.lastModifiedDate,
        // 스마트링 보유 여부. 다른 클러스터라 목록 조회 뒤 서버가 붙여 준다.
        // 기능이 꺼져 있거나 조회에 실패하면 이 키가 없다 — 그때는 아이콘만
        // 안 보이고 목록은 그대로다.
        has_smart_ring: item.hasSmartRing === true,
        bloodGlucose: null,
        bloodGlucoseMeal: null,
        bloodPressure: {},
        bmi: null,
        latestDate: item.latestMeasureDate ? new Date(item.latestMeasureDate) : new Date(0)
    };

    (item.measurements || []).forEach((measurement) => {
        if (!allowedTypes.includes(measurement.measureType)) return;

        // BMI 만 숫자로 다루던 기존 처리를 유지한다.
        const value = measurement.measureType === 'BMI'
            ? parseFloat(measurement.currentValue)
            : measurement.currentValue;
        const entry = {
            value,
            before_value: measurement.beforeValue,
            risk: getRiskLevel(value, measurement.measureType),
            measure_date: measurement.measureDate
        };

        switch (measurement.measureType) {
            case 'BLOOD_GLUCOSE': row.bloodGlucose = entry; break;
            case 'BLOOD_GLUCOSE_MEAL': row.bloodGlucoseMeal = entry; break;
            case 'BP_SYSTOLIC': row.bloodPressure.systolic = entry; break;
            case 'BP_DIASTOLIC': row.bloodPressure.diastolic = entry; break;
            case 'BMI': row.bmi = entry; break;
            default: break;
        }
    });

    return row;
};

// scopeParams 는 각 페이지가 localStorage 에서 만들어 넘기던 것과 같은 모양
// (is_super_admin / user_group_ids / group_ids / hospital_id ...) 이다.
export const fetchPatientsPage = async ({
    token,
    scopeParams,
    page = 1,
    size = PATIENTS_PAGE_SIZE,
    search,
    appUserIds
}) => {
    const params = { ...scopeParams, page, size };
    if (search) params.search = search;
    if (appUserIds !== undefined) params.appUserIds = appUserIds;

    const response = await axios.get(`${API_BASE_URL}/api/patients`, {
        headers: { Authorization: `Bearer ${token}` },
        params
    });
    return response.data;
};

// ControlCenter 는 그리드에 범위 전원을 한 번에 그린다. 사용자당 1행이고
// 지표별 최신값이 함께 오므로, 탭을 바꿔도 다시 받을 필요가 없다.
export const fetchPatientRoster = async ({ token, scopeParams }) => {
    const response = await axios.get(`${API_BASE_URL}/api/patients/roster`, {
        headers: { Authorization: `Bearer ${token}` },
        params: scopeParams
    });
    return response.data;
};

// 대상자 1명의 측정 이력. 모달을 열 때만 부른다.
// metric 은 배열이다 — 혈압 탭이 수축기와 이완기를 한 차트에 그린다.
export const fetchPatientMeasurements = async ({ token, scopeParams, appUserId, metrics, limit }) => {
    const params = { ...scopeParams };
    if (metrics && metrics.length > 0) params.metric = metrics.join(',');
    if (limit) params.limit = limit;

    const response = await axios.get(`${API_BASE_URL}/api/patients/${appUserId}/measurements`, {
        headers: { Authorization: `Bearer ${token}` },
        params
    });
    return response.data;
};

// 기록 악화 목록. 대상자별 최신·직전 원시 값 쌍만 오고, 등급 판정은 화면이 한다.
// metric 은 하나만 받는다 — 악화는 같은 지표끼리 비교하는 개념이다.
export const fetchPatientWorsening = async ({ token, scopeParams, metric }) => {
    const response = await axios.get(`${API_BASE_URL}/api/patients/worsening`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...scopeParams, metric }
    });
    return response.data;
};

// 각 페이지가 localStorage 를 뒤져 만들던 범위 파라미터. 세 화면이 같은 코드를
// 복사해 쓰고 있었다 (Dashboard.js / ControlCenter.js:231-279).
//
// ⚠️ 여기 값들은 브라우저가 만들어 보내는 것이고 서버가 JWT 로 다시 계산하지 않는다
//    (docs/refactoring.md §7). 옮겨 놓기만 했을 뿐 신뢰 경계는 그대로다.
export const buildScopeParams = ({ filterType = 'all' } = {}) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}') || {};
    const isSuperAdmin = user.is_super_admin === 'true' || localStorage.getItem('is_super_admin') === 'true';
    if (isSuperAdmin) return { is_super_admin: 'true' };

    let userGroupIds = user.user_group_ids || localStorage.getItem('user_group_ids');
    if (typeof userGroupIds === 'string' && userGroupIds.startsWith('[')) {
        try {
            userGroupIds = JSON.parse(userGroupIds);
        } catch (e) {
            // 원본과 같이 파싱 실패는 무시하고 문자열 그대로 쓴다.
        }
    }
    const userGroupIdsParam = Array.isArray(userGroupIds) ? userGroupIds.join(',') : userGroupIds;
    if (userGroupIdsParam) return { user_group_ids: userGroupIdsParam };

    const groupIds = localStorage.getItem('group_ids');
    if (groupIds) {
        return { group_ids: Array.isArray(groupIds) ? groupIds.join(',') : groupIds };
    }

    const hospitalId = user.hospital_id || localStorage.getItem('hospital_id');
    const regionCode = user.region_code || localStorage.getItem('region_code');
    const districtCode = user.district_code || localStorage.getItem('district_code');
    if (filterType === 'region') return { region_code: regionCode, district_code: districtCode };
    if (filterType === 'hospital') return { hospital_id: hospitalId };
    return { hospital_id: hospitalId, region_code: regionCode, district_code: districtCode };
};

export const fetchPatientRiskStats = async ({ token, scopeParams }) => {
    const response = await axios.get(`${API_BASE_URL}/api/patients/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: scopeParams
    });
    return response.data;
};
